import React, { useState } from "react";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd } from "@/components/Seo";
import { useContent } from "@/hooks/useContent";
import { OWNER_TEXTS } from "@/lib/contentDefaults";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";

export default function EntrustProperty() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", address: "",
    property_type: "", surface: "", bedrooms: "", capacity: "",
    already_operated: "", airbnb_listing: "", comment: "",
  });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const c = useContent("owner_texts", OWNER_TEXTS);
  const businessLd = useLocalBusinessLd();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  if (done) return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-32 text-center">
        <CheckCircle2 className="w-16 h-16 text-nj-primary mx-auto" />
        <h1 className="font-serif font-light text-3xl text-nj-text mt-6">Merci !</h1>
        <p className="text-nj-text2 mt-3">Votre demande d'estimation a bien été transmise à NJ Conciergerie. Nous vous recontactons sous 24h.</p>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <Seo title="Confier mon logement à Rouen | Estimation gratuite | NJ Conciergerie"
        description="Confiez votre logement à NJ Conciergerie à Rouen et recevez une estimation gratuite de vos revenus en location courte durée. Sans engagement."
        canonical="/confier-mon-logement" jsonLd={businessLd} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">{c.entrust_eyebrow}</span>
        <h1 className="font-serif font-light text-4xl text-nj-text mt-3">{c.entrust_title}</h1>
        <p className="text-nj-text2 mt-3">{c.entrust_subtitle}</p>

        <form onSubmit={submit} className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="lead-form">
          <div><Label>Prénom *</Label><Input required data-testid="lead-firstname" value={form.first_name} onChange={set("first_name")} className="mt-1.5" /></div>
          <div><Label>Nom *</Label><Input required data-testid="lead-lastname" value={form.last_name} onChange={set("last_name")} className="mt-1.5" /></div>
          <div><Label>Téléphone *</Label><Input required data-testid="lead-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
          <div><Label>Email *</Label><Input required type="email" data-testid="lead-email" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Adresse du logement</Label><Input value={form.address} onChange={set("address")} className="mt-1.5" /></div>
          <div><Label>Type de logement</Label><Input placeholder="Appartement, studio, maison…" value={form.property_type} onChange={set("property_type")} className="mt-1.5" /></div>
          <div><Label>Surface (m²)</Label><Input value={form.surface} onChange={set("surface")} className="mt-1.5" /></div>
          <div><Label>Nombre de chambres</Label><Input value={form.bedrooms} onChange={set("bedrooms")} className="mt-1.5" /></div>
          <div><Label>Capacité (voyageurs)</Label><Input value={form.capacity} onChange={set("capacity")} className="mt-1.5" /></div>
          <div><Label>Déjà exploité en courte durée ?</Label><Input placeholder="Oui / Non" value={form.already_operated} onChange={set("already_operated")} className="mt-1.5" /></div>
          <div><Label>Annonce Airbnb existante ?</Label><Input placeholder="Oui / Non" value={form.airbnb_listing} onChange={set("airbnb_listing")} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Commentaire</Label><Textarea rows={4} value={form.comment} onChange={set("comment")} className="mt-1.5" /></div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={loading} data-testid="lead-submit" className="bg-nj-primary hover:bg-nj-primaryHover text-white rounded-sm px-8 py-6 text-base">
              {loading ? "Envoi…" : "Demander mon estimation"}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
